import {
  kindForApi,
  type AdminModel,
  type Modality,
  type ModelApi,
  type ModelState,
  type ModelWrite,
  type OpenRouterRouting,
  type VendorSnapshot,
} from "@/types/modelRegistry";

export interface RoutingForm {
  order: string[];
  allowFallbacks: boolean;
  sort: "" | "price" | "latency" | "throughput";
  dataCollection: "" | "allow" | "deny";
  zdr: boolean;
}

export interface ModelForm {
  slug: string;
  providerId: string;
  modelName: string;
  api: ModelApi;
  name: string;
  description: string;
  reasoning: "" | "yes" | "no";
  input: Modality[];
  contextWindow: string;
  maxOutputTokens: string;
  costInput: string;
  costOutput: string;
  state: ModelState;
  displayOrder: string;
  routing: RoutingForm;
}

export const emptyRouting = (): RoutingForm => ({
  order: [],
  allowFallbacks: true,
  sort: "",
  dataCollection: "",
  zdr: false,
});

export const slugFrom = (modelName: string): string =>
  modelName
    .split("/")
    .pop()
    ?.toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "") ?? "";

export const emptyForm = (providerId: string): ModelForm => ({
  slug: "",
  providerId,
  modelName: "",
  api: "openai-completions",
  name: "",
  description: "",
  reasoning: "",
  input: [],
  contextWindow: "",
  maxOutputTokens: "",
  costInput: "",
  costOutput: "",
  state: "active",
  displayOrder: "0",
  routing: emptyRouting(),
});

const text = (value: number | null | undefined): string =>
  value === null || value === undefined ? "" : String(value);

const chatCostOf = (cost: AdminModel["cost"] | undefined) =>
  cost && "input" in cost ? cost : null;

const routingFrom = (routing: OpenRouterRouting | undefined): RoutingForm => ({
  order: routing?.order ?? [],
  allowFallbacks: routing?.allowFallbacks ?? true,
  sort: routing?.sort ?? "",
  dataCollection: routing?.dataCollection ?? "",
  zdr: routing?.zdr ?? false,
});

/** The form shows the effective values; what the vendor said is applied again at save time. */
export function formFrom(model: AdminModel): ModelForm {
  const cost = chatCostOf(model.cost);
  return {
    slug: model.slug,
    providerId: model.provider.id,
    modelName: model.modelName,
    api: model.api,
    name: model.name,
    description: model.description ?? "",
    reasoning: model.reasoning ? "yes" : "no",
    input: model.input,
    contextWindow: text(model.contextWindow),
    maxOutputTokens: text(model.maxOutputTokens),
    costInput: text(cost?.input),
    costOutput: text(cost?.output),
    state: model.state,
    displayOrder: String(model.displayOrder),
    routing: routingFrom(model.settings?.providerOptions?.openrouter),
  };
}

/** Fills the fields the admin left blank with what the vendor reports. Typed values stay. */
export function prefillFromVendor(
  form: ModelForm,
  vendor: VendorSnapshot,
): ModelForm {
  const cost = chatCostOf(vendor.cost);
  return {
    ...form,
    description: form.description || (vendor.description ?? ""),
    reasoning:
      form.reasoning ||
      (vendor.reasoning === undefined ? "" : vendor.reasoning ? "yes" : "no"),
    input: form.input.length > 0 ? form.input : (vendor.input ?? []),
    contextWindow: form.contextWindow || text(vendor.contextWindow),
    maxOutputTokens: form.maxOutputTokens || text(vendor.maxOutputTokens),
    costInput: form.costInput || text(cost?.input),
    costOutput: form.costOutput || text(cost?.output),
  };
}

const numberOrNull = (value: string): number | null =>
  value.trim() === "" ? null : Number(value);

/** Null when the admin's value only repeats the vendor's, so the sync keeps owning it. */
const unlessVendor = <T>(
  value: T | null,
  vendorValue: T | null | undefined,
): T | null => (value !== null && value === vendorValue ? null : value);

const sameList = (
  a: readonly string[],
  b: readonly string[] | undefined,
): boolean => !!b && a.length === b.length && a.every((x) => b.includes(x));

function routingToWrite(routing: RoutingForm): OpenRouterRouting | undefined {
  const out: OpenRouterRouting = {};
  if (routing.order.length > 0) out.order = routing.order;
  if (!routing.allowFallbacks) out.allowFallbacks = false;
  if (routing.sort) out.sort = routing.sort;
  if (routing.dataCollection) out.dataCollection = routing.dataCollection;
  if (routing.zdr) out.zdr = true;
  return Object.keys(out).length > 0 ? out : undefined;
}

export function toWrite(
  form: ModelForm,
  vendor: VendorSnapshot | undefined,
  viaOpenRouter: boolean,
): ModelWrite {
  const vendorCost = chatCostOf(vendor?.cost);
  const input = unlessVendor(numberOrNull(form.costInput), vendorCost?.input);
  const output = unlessVendor(
    numberOrNull(form.costOutput),
    vendorCost?.output,
  );
  const reasoning = form.reasoning === "" ? null : form.reasoning === "yes";
  const routing = viaOpenRouter ? routingToWrite(form.routing) : undefined;
  return {
    slug: form.slug,
    providerId: form.providerId,
    modelName: form.modelName,
    api: form.api,
    kind: kindForApi(form.api),
    name: form.name,
    description: unlessVendor(
      form.description.trim() || null,
      vendor?.description,
    ),
    reasoning: unlessVendor(reasoning, vendor?.reasoning),
    input: sameList(form.input, vendor?.input) ? [] : form.input,
    contextWindow: unlessVendor(
      numberOrNull(form.contextWindow),
      vendor?.contextWindow,
    ),
    maxOutputTokens: unlessVendor(
      numberOrNull(form.maxOutputTokens),
      vendor?.maxOutputTokens,
    ),
    cost:
      input !== null || output !== null
        ? {
            input: input ?? vendorCost?.input ?? 0,
            output: output ?? vendorCost?.output ?? 0,
          }
        : null,
    settings: routing ? { providerOptions: { openrouter: routing } } : null,
    state: form.state,
    displayOrder: Number(form.displayOrder) || 0,
  };
}

export const isComplete = (form: ModelForm): boolean =>
  !!(form.slug && form.providerId && form.modelName && form.name);
